<base-site-layout
    [heading]="heading | translate"
    [headerIcon]="icon"
    [isDialog]="true"
    (dialogCloseClicked)="close()"
>
    <p class="text-muted small mb-3">
        {{
            'Wählen Sie einen oder mehrere Buchungskreise und eine oder mehrere Warengruppen. Alle gewählten Warengruppen werden allen gewählten Buchungskreisen zugewiesen.'
                | translate
        }}
    </p>

    @if (saveError(); as error) {
        <div class="alert alert-danger d-flex align-items-start gap-2 mb-3" role="alert" data-cy="BULK_WG_SAVE_ERROR">
            <em class="fas fa-circle-exclamation pt-1"></em>
            <div>
                <strong>{{ 'Speichern fehlgeschlagen' | translate }}</strong>
                <div class="small">{{ error }}</div>
            </div>
        </div>
    }

    <div class="row g-3">
        <!-- Company codes multi-select -->
        <div class="col-12 col-md-6">
            <div class="card mg-panel">
                <div class="card-header bg-light d-flex align-items-center justify-content-between gap-2">
                    <span class="fw-semibold"><em class="fas fa-building pe-1"></em>{{ 'Buchungskreise' | translate }}</span>
                    <button
                        type="button"
                        class="btn btn-sm btn-primary text-nowrap"
                        data-cy="BULK_WG_SELECT_ALL_CC"
                        [disabled]="!canSelectAllCompanyCodes()"
                        (click)="selectAllCompanyCodes()"
                    >
                        {{ 'Alle Auswählen' | translate }}
                    </button>
                </div>
                <div class="mg-list">
                    @for (companyCode of companyCodes; track companyCode.id) {
                        <label class="mg-row" [class.mg-row--pending-add]="isCompanyCodeSelected(companyCode.id)">
                            <input
                                type="checkbox"
                                [attr.data-cy]="'BULK_WG_CC_' + companyCode.code"
                                [checked]="isCompanyCodeSelected(companyCode.id)"
                                [disabled]="saving()"
                                (change)="toggleCompanyCode(companyCode.id)"
                            />
                            <em class="fas fa-building text-muted"></em>
                            <span class="fw-semibold font-monospace">{{ companyCode.code }}</span>
                            @if (companyCode.name) {
                                <span class="text-muted">–</span>
                                <span class="flex-grow-1">{{ companyCode.name }}</span>
                            }
                        </label>
                    }
                </div>
            </div>
        </div>

        <!-- Material groups multi-select -->
        <div class="col-12 col-md-6">
            <div class="card mg-panel">
                <div class="card-header bg-light d-flex align-items-center gap-2">
                    <span class="fw-semibold"><em class="fas fa-sitemap pe-1"></em>{{ 'Warengruppen' | translate }}</span>
                </div>
                <div class="card-body d-flex flex-column gap-2">
                    <div class="d-flex gap-2">
                        <input
                            type="search"
                            class="form-control form-control-sm flex-grow-1"
                            data-cy="BULK_WG_MG_FILTER"
                            [formControl]="filterControl"
                            [placeholder]="'Filtern…' | translate"
                        />
                        <button
                            type="button"
                            class="btn btn-sm btn-primary text-nowrap"
                            data-cy="BULK_WG_SELECT_ALL_MG"
                            [disabled]="!canSelectAllVisibleMaterialGroups()"
                            (click)="selectAllVisibleMaterialGroups()"
                        >
                            {{ 'Alle Auswählen' | translate }}
                        </button>
                    </div>
                    <div class="mg-list">
                        @if (loading()) {
                            <div class="text-muted fst-italic small py-3 text-center">
                                {{ 'Warengruppen werden geladen...' | translate }}
                            </div>
                        } @else if (filteredMaterialGroups().length === 0) {
                            <div class="text-muted fst-italic small py-3 text-center">
                                {{ 'Keine Warengruppen verfügbar.' | translate }}
                            </div>
                        } @else {
                            @for (materialGroup of filteredMaterialGroups(); track materialGroup.id) {
                                <label
                                    class="mg-row"
                                    [class.mg-row--pending-add]="isMaterialGroupSelected(materialGroup.code)"
                                >
                                    <input
                                        type="checkbox"
                                        [attr.data-cy]="'BULK_WG_MG_' + materialGroup.code"
                                        [checked]="isMaterialGroupSelected(materialGroup.code)"
                                        [disabled]="saving()"
                                        (change)="toggleMaterialGroup(materialGroup.code)"
                                    />
                                    <em class="fas fa-sitemap text-muted"></em>
                                    <span class="fw-semibold font-monospace">{{ materialGroup.code }}</span>
                                    <span class="text-muted">–</span>
                                    <span class="flex-grow-1">{{ materialGroup.primaryName || materialGroup.code }}</span>
                                </label>
                            }
                        }
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="d-flex justify-content-between align-items-center mt-3 gap-2 flex-wrap">
        <div class="small text-muted" data-cy="BULK_WG_SUMMARY">
            @if (assignmentCount() > 0) {
                <em class="fas fa-plus pe-1 text-success"></em>
                {{ selectedCompanyCodeCount() }} {{ 'Buchungskreise' | translate }} ×
                {{ selectedMaterialGroupCount() }} {{ 'Warengruppen' | translate }} =
                <span class="fw-semibold">{{ assignmentCount() }}</span> {{ 'Zuordnungen' | translate }}
            } @else {
                {{ 'Bitte mindestens einen Buchungskreis und eine Warengruppe auswählen.' | translate }}
            }
        </div>
        <div class="d-flex gap-2">
            <button
                type="button"
                class="btn btn-outline-secondary"
                data-cy="BULK_WG_CANCEL"
                (click)="close()"
                [disabled]="saving()"
            >
                <em class="fas fa-xmark pe-1"></em>
                {{ 'Abbrechen' | translate }}
            </button>
            <button
                type="button"
                class="btn btn-primary"
                data-cy="BULK_WG_SAVE"
                [disabled]="!canSave()"
                (click)="save()"
            >
                <em class="fas fa-check pe-1"></em>
                {{ 'Speichern' | translate }}
            </button>
        </div>
    </div>
</base-site-layout>
